import React, { useEffect, useMemo } from "react";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormSelect from "Common/Custom/FormSelect";
import FormInput from "Common/Custom/FormInput";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import FormNumberInput from "Common/Custom/FormNumberInput";
import PropTypes from "prop-types";

const EditPoolBookingForm = ({
    fields = [],
    values = {},
    errors = {},
    handleChange = () => { },
    handleSubmit = () => { },
    loading = false,
    PooNameOptions = [],
    CustomerOpctions = [],
}) => {
    const isPackageSelected = Boolean(values.p_pack_id);

    /* ---------- AUTO TOTAL ---------- */
    useEffect(() => {
        if (!values.price) return;
        const totalAmount = Number(values.price) * Number(values.units);
        // Only update if value actually changed — avoids infinite loop
        if (Number(values.amount) !== totalAmount) {
            handleChange({ target: { name: "amount", value: totalAmount } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.price, values.units]);

    /* ---------- OPTIONS ---------- */
    const poolOptions = useMemo(
        () =>
            PooNameOptions.map(p => ({
                value: p.pool_package_id,
                label: p.pool_package_name,
            })),
        [PooNameOptions]
    );

    const customerOptions = useMemo(
        () =>
            CustomerOpctions.map(c => ({
                value: c.customer_id,
                label: c.customer_name,
            })),
        [CustomerOpctions]
    );

    /* ---------- FIELD RENDER ---------- */
    const renderField = (field) => {
        const isCustomer = field.name === "customer_id";
        const isPackage = field.name === "p_pack_id";
        const isUnits = field.name === "units";
        const isPrice = field.name === "price";
        const isAmount = field.name === "amount";
        const isPaymentStatus = field.name === "payment_status";

        /* ---------- DISABLE RULES ---------- */
        let disabled = false;

        // Customer is ALWAYS disabled in edit
        if (isCustomer) disabled = true;

        // Price & Amount always readonly
        if (isPrice || isAmount) disabled = true;

        // Payment status enabled only when required fields exist
        if (isPaymentStatus) {
            disabled = !(
                values.p_pack_id &&
                values.customer_id &&
                values.units &&
                Number(values.units) > 0
            );
        }

        /* ---------- OPTIONS ---------- */
        const getOptions = () => {
            switch (field.name) {
                case "customer_id":
                    return customerOptions;

                case "p_pack_id":
                    return poolOptions;

                case "payment_status":
                case "booking_status":
                    return field.options || [];

                default:
                    return [];
            }
        };

        /* ---------- SELECT ---------- */
        if (field.type === "select") {
            return (
                <FormSelect
                    key={field.name}
                    field={field}
                    disabled={disabled}
                    value={values[field.name]}
                    handleChange={handleChange}
                    options={getOptions()}
                    error={errors[field.name]}
                />
            );
        }

        /* ---------- UNITS ---------- */
        if (isUnits) {
            return (
                <FormInput
                    key={field.name}
                    field={{ ...field, disabled: !values.p_pack_id }}
                    value={values[field.name]}
                    handleChange={handleChange}
                    error={errors[field.name]}
                />
            );
        }

        /* ---------- DEFAULT ---------- */
        return (
            <FormInput
                key={field.name}
                field={{ ...field, disabled }}
                value={values[field.name] || ""}
                handleChange={handleChange}
                error={errors[field.name]}
            />
        );
    };


    return (
        <PageBody
            headerchildren={
                <HeadingOne>Update Pool Booking</HeadingOne>
            }
            onSubmit={handleSubmit}
        >
            {fields.map(renderField)}

            <FormSubmitButton
                label="Update"
                loading={loading}
                colSize="col-12"
                align="end"
                disabled={
                    !values.p_pack_id ||
                    !values.customer_id ||
                    !values.units ||
                    values.units <= 0 ||
                    loading
                }
            />
        </PageBody>
    );
};

export default EditPoolBookingForm;
